import { DatatableCell, DatatableRow } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { TABLE_COL_WIDTH } from '@/shared/constants';

import { collectAllTools } from '../AgentTree';
import { ToolConfigurationCell } from './ToolConfigurationCell';
import { ToolNameCell } from './ToolNameCell';
import { ToolRowActions } from './ToolRowActions';
import { WorkspaceAccessCell } from './WorkspaceAccessCell';
import type { ToolsTableV2RowProps } from './ToolsTableV2.types';

export function ToolsTableV2Row({
  tool,
  showType,
  isManaged,
  isExternalToolsAgent,
  onManageWorkspaces,
  onEditAgent,
  onDeleteAgent,
}: ToolsTableV2RowProps) {
  const { t } = useTranslation('admin');

  const status = tool.configurationStatus;
  const unconfiguredCount = status?.unconfiguredTools.length ?? 0;
  // The tree is the authority once it is there; the outstanding count is the only signal left
  // when it is not.
  const hasConfigurable = status?.agentTree
    ? collectAllTools(status.agentTree).some((child) => child.isConfigurable)
    : unconfiguredCount > 0;

  return (
    // Not interactive: the row leads nowhere, so it gets the hover tint without the pointer
    // the design system's `interactive` variant would also bring. The tween comes from the
    // primitive, which already transitions its own colours.
    <DatatableRow state="default" className="h-[72px] hover:[&>*]:bg-muted">
      <DatatableCell>
        <ToolNameCell tool={tool} />
      </DatatableCell>

      <DatatableCell>
        <ToolConfigurationCell
          hasConfigurable={hasConfigurable}
          allHaveOrgConfig={status?.allHaveOrgConfig ?? false}
          fullyConfiguredWorkspaces={tool.fullyConfiguredWorkspaces}
          unconfiguredCount={unconfiguredCount}
        />
      </DatatableCell>

      {showType && (
        <DatatableCell>
          <span className="text-body-md">
            {tool.source === 'user' ? t('admin.toolsTable.custom') : t('admin.toolsTable.system')}
          </span>
        </DatatableCell>
      )}

      <DatatableCell>
        <WorkspaceAccessCell
          workspaceAccess={tool.workspaceAccess}
          workspaces={tool.workspaces}
          {...(tool.builtAgent?.accessLabel ? { label: tool.builtAgent.accessLabel } : {})}
        />
      </DatatableCell>

      <DatatableCell className={TABLE_COL_WIDTH.actions}>
        <ToolRowActions
          tool={tool}
          hasConfigurable={hasConfigurable}
          isManaged={isManaged}
          isExternalToolsAgent={isExternalToolsAgent}
          onManageWorkspaces={onManageWorkspaces}
          onEditAgent={onEditAgent}
          onDeleteAgent={onDeleteAgent}
        />
      </DatatableCell>
    </DatatableRow>
  );
}
